"use client";

import { useCallback } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Clock } from "@/components/icons";
import { Pagination } from "@/components/ui/pagination";
import { LoadingSpinner, OverlayLoadingSpinner } from "@/components/ui";
import { fetchAuthorPosts } from "./fetch";

export default function AuthorPostsList({ uuid }: { uuid: string }) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const currentPage = Math.max(1, Number(searchParams.get("page") ?? "1"));

  const setPage = useCallback(
    (page: number) => {
      const params = new URLSearchParams(searchParams.toString());
      if (page <= 1) params.delete("page");
      else params.set("page", String(page));
      router.push(`?${params.toString()}`, { scroll: false });
    },
    [router, searchParams]
  );

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["author-posts", uuid, currentPage],
    queryFn: () => fetchAuthorPosts(uuid, currentPage),
    placeholderData: keepPreviousData,
  });

  const posts = data?.posts ?? [];
  const totalPages = data?.totalPages ?? 1;
  const totalResults = data?.totalResults ?? 0;

  const handlePageChange = (page: number) => {
    setPage(page);
    const el = document.getElementById("author-posts");
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  if (isLoading) {
    return <LoadingSpinner label="Loading articles…" />;
  }

  return (
    <div id="author-posts" className="relative">
      <p className="text-muted-foreground mb-4 text-xs">
        {totalResults} article{totalResults !== 1 ? "s" : ""} published
      </p>

      {isFetching && !isLoading && <OverlayLoadingSpinner />}

      <div
        className={
          isFetching
            ? "pointer-events-none opacity-40 transition-opacity duration-200"
            : "transition-opacity duration-200"
        }
      >
        {posts.length === 0 ? (
          <p className="text-muted-foreground text-sm">No published articles yet.</p>
        ) : (
          <div className="divide-border divide-y">
            {posts.map((post) => (
              <article key={post.href} className="group relative py-8">
                <div className="flex items-start gap-5">
                  {post.image && (
                    <div className="hidden shrink-0 sm:block">
                      <Image
                        src={post.image}
                        alt={post.title}
                        width={1200}
                        height={628}
                        sizes="120px"
                        className="border-border h-[63px] w-[120px] rounded-lg border object-cover"
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="text-muted-foreground border-border rounded-full border px-2 py-0.5 text-xs font-medium">
                        {post.category}
                      </span>
                      <span className="text-muted-foreground flex items-center gap-1 text-xs">
                        <Clock size={10} />
                        {post.date}
                      </span>
                    </div>
                    <h2 className="text-foreground mb-2 text-base leading-snug font-bold sm:text-lg">
                      <Link
                        href={post.href}
                        className="underline-offset-2 after:absolute after:inset-0 hover:underline"
                      >
                        {post.title}
                      </Link>
                    </h2>
                    {post.excerpt && (
                      <p className="text-muted-foreground mb-3 line-clamp-2 text-sm leading-relaxed">
                        {post.excerpt}
                      </p>
                    )}
                    <span className="text-muted-foreground group-hover:text-foreground inline-flex items-center gap-1 text-xs font-semibold transition-colors">
                      Read article
                      <ArrowRight
                        size={12}
                        className="transition-transform group-hover:translate-x-0.5"
                      />
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <Pagination current={currentPage} total={totalPages} onChange={handlePageChange} />
      </div>
    </div>
  );
}
